import { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import {
  FileDown,
  FilePenLine,
  Pencil,
  Send,
  Trash2,
} from 'lucide-react';

import { deleteDraftQuotation } from '#/api/crmMgmt/quotation/deleteDraft';
import { downloadQuotationExcel } from '#/api/crmMgmt/quotation/downloadExcel';
import { issueQuotation } from '#/api/crmMgmt/quotation/issue';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import QuotationIssueDialog from '#/modules/crmMgmt/quotation/components/QuotationIssueDialog';
import {
  getQuotationApiErrorMessage,
  isQuotationVersionConflict,
} from '#/modules/crmMgmt/quotation/helper/apiError';
import {
  canDeleteDraftQuotation,
  canEditQuotationDraft,
  canIssueQuotation,
  canUpdateQuotationInternalRemarkOnly,
} from '#/modules/crmMgmt/quotation/helper/quotationStatus';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { useCan } from '#/modules/infra/auth/useCan';
import { getFileTransferErrorMessage } from '#/utils/blobResponseError';
import { triggerBlobDownload } from '#/utils/downloadBlob';
import {
  quotationDetailQueryKey,
  quotationListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

/** 報價單指令：草稿編輯／發出／刪除；已發出或過期改內部備註；任一狀態下載 Excel。 */
const QuotationCommandBar = ({ detail, onRefetch }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [issueOpen, setIssueOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const canUpdate = useCan(PERM.QUOTATION_UPDATE);

  const { id, status } = detail;
  const updatedAt = detail.record?.updatedAt;

  const commandMutation = useMutation({
    retry: false,
    mutationFn: ({ action, validUntil }) => (
      action === 'issue'
        ? issueQuotation(id, validUntil ? { updatedAt, validUntil } : { updatedAt })
        : deleteDraftQuotation(id, { updatedAt })
    ),
    onSuccess: async (result, { action }) => {
      toast.success(result?.message || '操作成功');
      await queryClient.invalidateQueries({ queryKey: quotationListQueryKeyPrefix() });
      if (action === 'delete') {
        navigate(ROUTES.quotation);
        return;
      }
      setIssueOpen(false);
      if (result?.data) {
        queryClient.setQueryData(quotationDetailQueryKey(id), result);
      }
      await onRefetch?.();
    },
    onError: async (error) => {
      toast.error(getQuotationApiErrorMessage(error));
      if (isQuotationVersionConflict(error)) {
        await onRefetch?.();
      }
    },
  });

  const isPending = commandMutation.isPending;

  async function handleDelete() {
    const ok = await confirm({
      title: '刪除草稿',
      message: '將刪除此報價草稿（不可復原）。確定刪除？',
      confirmText: '刪除草稿',
      destructive: true,
    });
    if (ok) {
      commandMutation.mutate({ action: 'delete' });
    }
  }

  async function handleDownload() {
    if (isDownloading) {
      return;
    }
    setIsDownloading(true);
    try {
      const { blob, filename } = await downloadQuotationExcel(id, detail.quotationNo || 'quotation');
      triggerBlobDownload(blob, filename);
    } catch (error) {
      toast.error(
        getFileTransferErrorMessage(error)
        || getQuotationApiErrorMessage(error, '下載 Excel 失敗'),
      );
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <>
      {ConfirmDialogHost}
      {canUpdate && canEditQuotationDraft(status) ? (
        <Button asChild className="document-detail-edit-action">
          <Link to={ROUTES.quotationEdit(id)}>
            <Pencil className="size-3.5" aria-hidden />
            編輯
          </Link>
        </Button>
      ) : null}
      {canUpdate && canIssueQuotation(status) ? (
        <Button type="button" disabled={isPending} onClick={() => setIssueOpen(true)}>
          <Send className="size-3.5" aria-hidden />
          發出
        </Button>
      ) : null}
      {canUpdate && canUpdateQuotationInternalRemarkOnly(status) ? (
        <Button asChild variant="outline">
          <Link to={ROUTES.quotationRemark(id)}>
            <FilePenLine className="size-3.5" aria-hidden />
            編輯內部備註
          </Link>
        </Button>
      ) : null}
      <Button type="button" variant="outline" disabled={isDownloading} onClick={handleDownload}>
        <FileDown className="size-3.5" aria-hidden />
        {isDownloading ? '下載中…' : '下載 Excel'}
      </Button>
      {canUpdate && canDeleteDraftQuotation(status) ? (
        <Button
          type="button"
          variant="destructive"
          className={DESTRUCTIVE_SOLID_BUTTON_CLASS}
          disabled={isPending}
          onClick={handleDelete}
        >
          <Trash2 className="size-3.5" aria-hidden />
          刪除草稿
        </Button>
      ) : null}
      {issueOpen ? (
        <QuotationIssueDialog
          open
          onOpenChange={setIssueOpen}
          isPending={isPending}
          onConfirm={(validUntil) => commandMutation.mutate({ action: 'issue', validUntil })}
        />
      ) : null}
    </>
  );
};

QuotationCommandBar.propTypes = {
  detail: PropTypes.object.isRequired,
  onRefetch: PropTypes.func,
};

export default QuotationCommandBar;
